/**
 * №13.1 (§2.7) — the arrangement picker. ONE component, driven by the invariant,
 * used identically at time capture (2.2), disbursement capture (2.6) and billing
 * (2.9): given a matter, if exactly one OPEN arrangement → no UI, auto-attributed;
 * if several → a dropdown with the default pre-selected, each option showing its
 * method badge; if none → an inert note (capture opens a General arrangement).
 *
 * A matter with several arrangements of different treatments is the designed-for
 * case, not an edge case.
 */
import { useEffect, useMemo } from "react";
import { useFeeArrangements, type EntityRow } from "../../hooks/use-time-billing";

const methodLabel = (m: unknown): string => (m === "fixed" ? "Fixed" : "T&M");

export interface ArrangementPickerProps {
  matterId: string | undefined;
  value: string;
  onChange: (arrangementId: string) => void;
  /** Add an "All arrangements" option (billing worksheet). */
  includeAll?: boolean;
  label?: string;
}

export function ArrangementPicker({ matterId, value, onChange, includeAll, label = "Arrangement" }: ArrangementPickerProps) {
  const arrangements = useFeeArrangements(matterId);
  const open = useMemo(
    () => (arrangements.data ?? []).filter((a) => (a.status ?? "open") === "open"),
    [arrangements.data],
  );
  const defaultId = useMemo(() => {
    const def = open.find((a) => a.is_default === "yes");
    return (def ?? open[0])?.id as string | undefined;
  }, [open]);

  // Invariant: exactly one open → auto-attribute (no UI). Emit it once.
  useEffect(() => {
    if (open.length === 1 && value !== (open[0]!.id as string)) onChange(open[0]!.id as string);
    if (open.length > 1 && !value && defaultId) onChange(defaultId);
  }, [open, value, defaultId, onChange]);

  if (arrangements.isLoading) return <div className="picker"><span className="muted tiny">Resolving arrangement…</span></div>;

  // None open → inert note; capture opens a General arrangement on first record.
  if (open.length === 0) {
    return (
      <div className="picker">
        <span className="muted">{label}:</span>
        <span className="auto">no open arrangement — capture opens a General arrangement</span>
      </div>
    );
  }

  // Exactly one → auto-attributed, no picker (show which, for legibility).
  if (open.length === 1) {
    const a = open[0]!;
    return (
      <div className="picker">
        <span className="muted">{label}:</span>
        <span>{(a.description as string) ?? a.id} <span className="badge badge--tm">{methodLabel(a.billing_method)}</span></span>
        <span className="auto">one open → auto-attributed</span>
      </div>
    );
  }

  // Many → dropdown, default pre-selected, method badge per option.
  return (
    <div className="picker">
      <span className="muted">{label}:</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        {open.map((a: EntityRow) => (
          <option key={a.id} value={a.id}>
            {(a.description as string) ?? a.id} ({methodLabel(a.billing_method)}){a.is_default === "yes" ? " — default" : ""}
          </option>
        ))}
        {includeAll ? <option value="__all__">All arrangements</option> : null}
      </select>
      <span className="auto">many → pick</span>
    </div>
  );
}
